import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Select } from 'antd';
import { useTranslation } from 'react-i18next';
import { useRole } from '../../store/roleContext';
import { Role, Permission } from '../../types/roles';
import { fetchPermissions } from '../../api/permissionApi';
import { translatePermission } from '../../utils/permissionTranslator';
import { useAuth } from '../../store/AuthContext';

interface RoleFormProps {
    editRole?: Role | null;
    onFinishEdit?: () => void;
}

const RoleForm: React.FC<RoleFormProps> = ({ editRole, onFinishEdit }) => {
    const [form] = Form.useForm();
    const { addRole, updateRole } = useRole();
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const { t } = useTranslation();
    const { hasPermission } = useAuth();

    useEffect(() => {
        fetchPermissions().then((data) => setPermissions(data));
    }, []);

    useEffect(() => {
        if (editRole) {
            form.setFieldsValue(editRole);
        } else {
            form.resetFields();
        }
    }, [editRole, form]);

    const onFinish = (values: Role) => {
        if (editRole) {
            updateRole({ ...editRole, ...values });
            onFinishEdit?.();
        } else {
            addRole(values);
            form.resetFields();
        }
    };

    return (
        <Form form={form} layout="vertical" onFinish={onFinish}>
            <Form.Item
                name="name"
                label={t('role_name')}
                rules={[{ required: true, message: t('please_enter_role_name') || '' }]}
            >
                <Input placeholder={t('role_name') || ''} />
            </Form.Item>

            <Form.Item name="permissions" label={t('select_permissions')}>
                <Select mode="multiple" placeholder={t('select_permissions') || ''}>
                    {permissions.map((perm) => (
                        <Select.Option key={perm.id} value={perm.id}>
                            {translatePermission(perm.name, t)} {/* <<-- use the translation utility */}
                        </Select.Option>
                    ))}
                </Select>
            </Form.Item>

            <Form.Item>
                <Button type="primary" htmlType="submit" block>
                    {editRole ? (hasPermission('role.update') && t('update_role')) : (hasPermission('role.create') && t('create_role'))}
                </Button>
            </Form.Item>
        </Form>
    );
};

export default RoleForm;
